import React from 'react'
import Pets from '@mui/icons-material/Pets'
import { Avatar, AvatarProps, Badge, IconButton, Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import Animal, { AnimalIcons } from '../constants/animal'
import BoopIcon from '../icons/Boop'
import UploadImageIcon from '../icons/UploadImage'
import i18n from '../locales/i18n'
import { ClassesType } from '../types'

const useStyles = makeStyles(
  theme => ({
    badge: {
      top: '18%',
      right: 8,
      color: theme.colors.badgeColor,
    },
    badgeIcon: {
      padding: 0,
    },
    editIcon: {
      color: theme.colors.tableBackground,
    },
    editIconButton: {
      width: 40,
      height: 40,
      backgroundColor: theme.colors.title,
      '&:hover': {
        backgroundColor: theme.colors.title,
      },
    },
    defaultIcon: {
      fontSize: 60,
      color: theme.colors.secondaryText,
      [theme.breakpoints.down('sm')]: {
        fontSize: 48,
      },
    },
    smallIcon: {
      fontSize: 37,
    },
    tinyIcon: {
      fontSize: 24,
    },
    root: {
      backgroundColor: theme.colors.patientAvatarBackground,
    },
  }),
  { name: 'PatientAvatar' },
)

export interface PatientAvatarProps extends AvatarProps {
  readonly animal?: Animal
  readonly classes?: ClassesType<typeof useStyles>
  readonly isLoading?: boolean
  readonly onEdit?: () => void
  readonly showBoopBadge?: boolean
  readonly showEditIcon?: boolean
  readonly small?: boolean
  readonly tiny?: boolean
}

const PatientAvatar = ({
  animal,
  src = '',
  classes: classesProp,
  showBoopBadge = false,
  showEditIcon = false,
  isLoading,
  className,
  small,
  tiny,
  onEdit,
  ...rest
}: PatientAvatarProps) => {
  const classes = useStyles({ classes: classesProp })

  const Icon = animal ? AnimalIcons[animal] || Pets : Pets

  if (isLoading) {
    return (
      <Skeleton
        className={classNames(classes.root, className)}
        variant="circular"
      />
    )
  }

  const content = (
    <Avatar
      alt={i18n.t('Common:PATIENT_PICTURE')}
      className={classNames(classes.root, className)}
      src={src}
      {...rest}
    >
      {!src && (
        <Icon
          className={classNames(classes.defaultIcon, {
            [classes.smallIcon]: small,
            [classes.tinyIcon]: tiny,
          })}
        />
      )}
    </Avatar>
  )

  if (showBoopBadge) {
    return (
      <Badge
        badgeContent={<BoopIcon className={classes.badgeIcon} />}
        classes={{ badge: classes.badge }}
      >
        {content}
      </Badge>
    )
  }

  if (showEditIcon) {
    return (
      <Badge
        badgeContent={
          <IconButton
            className={classNames(classes.badgeIcon, classes.editIconButton)}
            size="large"
            onClick={onEdit}
          >
            <UploadImageIcon className={classes.editIcon} />
          </IconButton>
        }
        classes={{ badge: classes.badge }}
      >
        {content}
      </Badge>
    )
  }

  return content
}

export default PatientAvatar
